import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faUpload, 
  faFileExcel, 
  faSpinner, 
  faCheckCircle,
  faExclamationTriangle,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import * as ExcelJS from 'exceljs';
import { importLines, LineImportResult } from '../../services/lineApi';
import { useToast } from '../ui/ToastProvider';

interface LineImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const LineImportModal: React.FC<LineImportModalProps> = ({ isOpen, onClose, onImportComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<LineImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const { showSuccess, showError } = useToast();

  if (!isOpen) return null;

  const handleFileSelect = (selectedFile: File) => {
    console.log('handleFileSelect called with:', selectedFile.name, selectedFile.type);
    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      showError('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls)');
      return;
    }
    console.log('Setting file state to:', selectedFile.name);
    setFile(selectedFile);
    setResults(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const parseExcelFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const buffer = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);
          const worksheet = workbook.worksheets[0];
          
          // Convert worksheet to JSON data
          const jsonData: any[] = [];
          const headers: string[] = [];
          
          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) {
              // Header row
              row.eachCell((cell, colNumber) => {
                headers[colNumber - 1] = cell.value?.toString() || `Column${colNumber}`;
              });
            } else {
              // Data rows
              const record: any = {};
              row.eachCell((cell, colNumber) => {
                const header = headers[colNumber - 1];
                if (header) {
                  record[header] = cell.value;
                }
              });
              if (Object.keys(record).length > 0) {
                jsonData.push(record);
              }
            }
          });
          
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleImport = async () => {
    if (!file) {
      showError('Bitte wählen Sie eine Datei aus');
      return;
    }

    try {
      setImporting(true);
      
      // Parse Excel file
      const rawData = await parseExcelFile(file);
      
      if (rawData.length === 0) {
        showError('Die Excel-Datei enthält keine Daten');
        return;
      }

      // Transform data to expected format
      const lineData = rawData.map((row: any) => ({
        _Value_DE: row._Value_DE || row['_Value_DE'] || ''
      })).filter(line => line._Value_DE.trim() !== '');

      if (lineData.length === 0) {
        showError('Keine gültigen Linien-Daten gefunden. Stellen Sie sicher, dass die Spalte "_Value_DE" vorhanden ist.');
        return;
      }

      // Import to backend
      const importResults = await importLines(lineData);
      setResults(importResults);
      
      showSuccess(`Linien-Import erfolgreich: ${importResults.created} erstellt, ${importResults.updated} aktualisiert`);
      onImportComplete();
      
    } catch (error) {
      console.error('Import error:', error);
      showError(error instanceof Error ? error.message : 'Fehler beim Importieren der Linien');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    const templateData = [
      { _Value_DE: 'Beispiel Linie 1' },
      { _Value_DE: 'Beispiel Linie 2' },
      { _Value_DE: 'Beispiel Linie 3' }
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Linien');
    
    // Add headers
    const headers = Object.keys(templateData[0]);
    worksheet.addRow(headers);
    
    // Add data rows
    templateData.forEach(row => {
      worksheet.addRow(Object.values(row));
    });
    
    // Download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'linien_template.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const resetModal = () => {
    setFile(null);
    setResults(null);
    setImporting(false);
    setDragOver(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon icon={faFileExcel} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Linien Import</h3>
                <p className="text-sm text-gray-600">Import und Verwaltung von Linien-Daten</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={importing}
              className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Template Download */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faDownload} className="text-blue-600" />
                <div>
                  <h4 className="font-medium text-blue-900">Excel-Vorlage</h4>
                  <p className="text-sm text-blue-700">
                    Laden Sie die Vorlage herunter und füllen Sie die Spalte "_Value_DE" aus
                  </p>
                </div>
              </div>
              <button
                onClick={downloadTemplate}
                disabled={importing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                Vorlage laden
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excel-Datei auswählen
              {file && <span className="text-green-600 ml-2">✓ Datei ausgewählt</span>}
            </label>
            
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver 
                  ? 'border-purple-400 bg-purple-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FontAwesomeIcon icon={faFileExcel} className="text-2xl text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-600">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="ml-4 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    disabled={importing}
                  >
                    Entfernen
                  </button>
                </div>
              ) : (
                <div>
                  <FontAwesomeIcon icon={faUpload} className="text-3xl text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">
                    Datei hier ablegen oder{' '}
                    <button
                      onClick={() => document.getElementById('file-input')?.click()}
                      className="text-purple-600 font-medium hover:text-purple-700 underline"
                      disabled={importing}
                    >
                      durchsuchen
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">
                    Unterstützte Formate: .xlsx, .xls
                  </p>
                </div>
              )}
            </div>
            
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) {
                  console.log('File selected:', selectedFile.name);
                  handleFileSelect(selectedFile);
                }
              }}
              className="hidden"
              disabled={importing}
            />
          </div>

          {/* Results */}
          {results && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-green-900 mb-2">Import erfolgreich</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-700">Erstellt:</span>
                      <span className="font-medium ml-1">{results.created}</span>
                    </div>
                    <div>
                      <span className="text-green-700">Aktualisiert:</span>
                      <span className="font-medium ml-1">{results.updated}</span>
                    </div>
                  </div>
                  {results.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-orange-700 font-medium text-sm mb-1">Warnungen:</p>
                      <ul className="text-xs text-orange-600 space-y-1">
                        {results.errors.slice(0, 5).map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                        {results.errors.length > 5 && (
                          <li>• ... und {results.errors.length - 5} weitere</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={importing}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {results ? 'Schließen' : 'Abbrechen'}
            </button>
            
            {!results && (
              <button
                onClick={handleImport}
                disabled={!file || importing}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition-colors ${
                  !file || importing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {importing && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                <FontAwesomeIcon icon={faUpload} />
                {importing ? 'Importiere...' : file ? 'Importieren' : 'Datei auswählen'}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LineImportModal;
